import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { rxResource } from '@angular/core/rxjs-interop';
import { map, firstValueFrom } from 'rxjs';
import type { EnvironmentListItem, EnvironmentResponse, UpdateEnvironment } from '@zag/control/models';

@Injectable({ providedIn: 'root' })
export class ControlEnvironmentService {
  private readonly http = inject(HttpClient);

  /** Signal to trigger reload of the environment list */
  private readonly reloadTrigger = signal(0);

  /** rxResource for loading environment list */
  readonly environmentsResource = rxResource({
    params: () => ({ trigger: this.reloadTrigger() }),
    stream: () => this.http.get<EnvironmentListItem[]>('/api/v1/environments').pipe(map(environments => ({ data: environments }))),
    defaultValue: { data: [] as EnvironmentListItem[] },
  });

  /** Computed: list of environments (unwrapped from resource) */
  readonly environments = computed(() => this.environmentsResource.value().data);

  /** Reload the environment list */
  reload(): void {
    this.reloadTrigger.update(v => v + 1);
  }

  /** Get environment details by slug */
  async getEnvironment(slug: string): Promise<EnvironmentResponse> {
    return firstValueFrom(this.http.get<EnvironmentResponse>(`/api/v1/environments/${slug}`));
  }

  /** Update an environment */
  async updateEnvironment(slug: string, data: UpdateEnvironment): Promise<EnvironmentResponse> {
    const result = await firstValueFrom(this.http.put<EnvironmentResponse>(`/api/v1/environments/${slug}`, data));
    this.reload();
    return result;
  }

  /** Patch only the default image tag on an nx-provider environment. */
  async updateDefaultImageTag(slug: string, defaultImageTag: string): Promise<EnvironmentResponse> {
    return firstValueFrom(this.http.patch<EnvironmentResponse>(`/api/v1/environments/${slug}/default-image-tag`, { defaultImageTag }));
  }

  /** Deactivate an environment */
  async deactivateEnvironment(slug: string): Promise<void> {
    await firstValueFrom(this.http.delete(`/api/v1/environments/${slug}`));
    this.reload();
  }
}
